import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  Slider,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import ViewerDialogContent from './ViewerDialogContent';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Replay10 as Replay10Icon,
  Forward10 as Forward10Icon,
  AudioFile as AudioFileIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Subtitles as SubtitlesIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewerFile } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '70vw',
    height: '60vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const AudioContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  backgroundColor: 'linear-gradient(135deg, #f0f0f0 0%, #ffffff 100%)',
  padding: '32px',
  paddingTop: '20px',
  overflowY: 'auto',
  gap: '16px',
  [theme.breakpoints.down('sm')]: {
    padding: '20px 16px',
  },
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(87, 87, 176, 0.1)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(87, 87, 176, 0.4)',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(87, 87, 176, 0.6)',
    },
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(87, 87, 176, 0.4) rgba(87, 87, 176, 0.1)',
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  width: '100%',
  maxWidth: '600px',
  flexShrink: 0,
}));

const PlaybackControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const TimeDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  fontSize: '14px',
  color: '#666',
  marginTop: '8px',
}));

const VolumeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: '120px',
}));

const TranscriptionContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '600px',
  marginTop: '16px',
  marginBottom: '20px',
  padding: '16px',
  backgroundColor: 'rgba(245, 245, 255, 0.9)',
  borderRadius: '12px',
  border: '1px solid rgba(87, 87, 176, 0.15)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },
}));

const TranscriptionText = styled(Box)(({ theme }) => ({
  fontSize: '0.9rem',
  lineHeight: '1.6',
  color: '#444',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderRadius: '8px',
  padding: '16px',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '120px',
  maxHeight: '300px',
  overflowY: 'scroll',
  overflowX: 'hidden',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  scrollBehavior: 'smooth',
  [theme.breakpoints.down('sm')]: {
    maxHeight: '220px',
  },
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(87, 87, 176, 0.1)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(87, 87, 176, 0.4)',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(87, 87, 176, 0.6)',
    },
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(87, 87, 176, 0.4) rgba(87, 87, 176, 0.1)',
}));

const AudioViewer = React.forwardRef(({ open, onClose, filename = '', eventId = null, filePath = '', timeevent = null }, _ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    fileBlob: audioBlob,
    fileUrl: audioUrl,
    loading,
    progress,
    error,
    setError,
    setLoading,
  } = useViewerFile({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.audio_missing_source', 'File id or path is required'),
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Audio playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(1);
  
  // Transcription state
  const [transcription, setTranscription] = useState('');
  const [transcriptionExpanded, setTranscriptionExpanded] = useState(true);
  const [transcriptionLoading, setTranscriptionLoading] = useState(false);
  const [hasTranscription, setHasTranscription] = useState(false);
  
  const containerRef = useRef(null);
  const audioRef = useRef(null);

  // Request transcription for the audio file
  const requestTranscription = useCallback(() => {
    if (!eventId && !timeevent) return;
    
    setTranscriptionLoading(true);
    
    // If we have timeevent data, use it directly
    if (timeevent && timeevent.eventdata) {
      setTranscriptionLoading(false);
      setHasTranscription(true);
      
      // Extract transcription from timeevent.eventdata
      let transcriptionText = '';
      
      // Check for various possible transcription fields
      if (timeevent.eventdata.transcription) {
        transcriptionText = timeevent.eventdata.transcription;
      } else if (timeevent.eventdata.text) {
        transcriptionText = timeevent.eventdata.text;
      } else if (timeevent.eventdata.content) {
        transcriptionText = timeevent.eventdata.content;
      } else if (timeevent.eventdata.description) {
        transcriptionText = timeevent.eventdata.description;
      } else {
        // If no direct transcription, show what we can from the event data
        const eventDataStr = JSON.stringify(timeevent.eventdata, null, 2);
        if (eventDataStr && eventDataStr !== '{}') {
          transcriptionText = `${eventDataStr}`;
        } else if (timeevent.eventdata) {
          transcriptionText = `Event Data:\n\n${timeevent.eventdata}`;
        } else {
          transcriptionText = t('viewer.no_transcription');
        }
      }
      
      setTranscription(transcriptionText);
      return;
    }
    
    // If we have timeevent but no structured eventdata, show raw data if available
    if (timeevent && timeevent.eventdata) {
      setTranscriptionLoading(false);
      setHasTranscription(true);
      setTranscription(`Event Data:\n\n${timeevent.eventdata}`);
      return;
    }

    if (timeevent && timeevent.eventdata) {
      setTranscriptionLoading(false);
      setHasTranscription(true);
      setTranscription(`Event Data:\n\n${timeevent.eventdata}`);
      return;
    }
    
    // Fallback to WebSocket request if no timeevent data
    setTimeout(() => {
      setTranscriptionLoading(false);
      setHasTranscription(true);
      setTranscription(t('viewer.no_transcription'));
    }, 1000);
  }, [eventId, timeevent, t]);

  // Format time helper function
  const formatTime = useCallback((time) => {
    if (isNaN(time)) return '0:00';
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setTranscription('');
    } else {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setTranscription('');
      
      // Request transcription if we have an eventId or timeevent data
      if (eventId || timeevent) {
        requestTranscription();
      }
    }
  }, [open, eventId, timeevent, requestTranscription]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = () => {
      setError('Failed to load audio file');
      setLoading(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl, setError, setLoading]);

  const downloadAudio = () => {
    if (audioBlob) {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = filename || 'audio';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Audio control functions
  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (event, newValue) => {
    const audio = audioRef.current;
    if (!audio) return;

    const seekTime = (newValue / 100) * duration;
    audio.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleVolumeChange = (event, newValue) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newVolume = newValue / 100;
    audio.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = previousVolume;
      setVolume(previousVolume);
      setIsMuted(false);
    } else {
      setPreviousVolume(volume);
      audio.volume = 0;
      setVolume(0);
      setIsMuted(true);
    }
  };

  const skipBackward = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.max(0, audio.currentTime - 10);
  };

  const skipForward = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.min(duration, audio.currentTime + 10);
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <AudioFileIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.audio_player_title')} - ${filename}` : t('viewer.audio_player_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadAudio} 
            disabled={!audioBlob}
            size="small"
            title={t('viewer.download_audio')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, mx: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('viewer.loading_audio')} {Math.round(progress)}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ flex: 1, maxWidth: 200 }}
            />
          </Box>
        )}
      </ToolbarContainer>

      <ViewerDialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'auto' }}>
        <AudioContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ width: 300, mb: 2 }}
              />
              <Typography color="text.secondary">
                {t('viewer.loading_audio')} {Math.round(progress)}%
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_audio')}
              </Typography>
              <Typography color="text.secondary">
                {error}
              </Typography>
            </Box>
          ) : audioUrl ? (
            <Box sx={{ 
              width: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 2,
              minHeight: 'min-content'
            }}>
              <audio ref={audioRef} src={audioUrl} preload="metadata" />

              <ControlsContainer>
                {/* Progress Slider */}
                <Box sx={{ width: '100%' }}>
                  <Slider
                    value={duration > 0 ? (currentTime / duration) * 100 : 0}
                    onChange={handleSeek}
                    sx={{
                      color: primaryColor,
                      '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                      },
                    }}
                  />
                  <TimeDisplay>
                    <Typography variant="body2">
                      {formatTime(currentTime)}
                    </Typography>
                    <Typography variant="body2">
                      {formatTime(duration)}
                    </Typography>
                  </TimeDisplay>
                </Box>

                {/* Playback Controls */}
                <PlaybackControls>
                  <IconButton onClick={skipBackward} disabled={!audioUrl}>
                    <Replay10Icon />
                  </IconButton>
                  
                  <IconButton 
                    onClick={togglePlayPause} 
                    disabled={!audioUrl}
                    sx={{
                      backgroundColor: primaryColor,
                      color: 'white',
                      width: 56,
                      height: 56,
                      '&:hover': {
                        backgroundColor: '#4a4a9a',
                      },
                      '&:disabled': {
                        backgroundColor: '#ccc',
                        color: '#666',
                      },
                    }}
                  >
                    {isPlaying ? <PauseIcon sx={{ fontSize: 32 }} /> : <PlayIcon sx={{ fontSize: 32 }} />}
                  </IconButton>
                  
                  <IconButton onClick={skipForward} disabled={!audioUrl}>
                    <Forward10Icon />
                  </IconButton>
                </PlaybackControls>

                {/* Volume Control */}
                <VolumeContainer>
                  <IconButton onClick={toggleMute} size="small">
                    {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                  </IconButton>
                  <Slider
                    value={volume * 100}
                    onChange={handleVolumeChange}
                    sx={{
                      color: primaryColor,
                      width: 80,
                      '& .MuiSlider-thumb': {
                        width: 12,
                        height: 12,
                      },
                    }}
                  />
                </VolumeContainer>
              </ControlsContainer>
              
              {/* Transcription Section */}
              <TranscriptionContainer>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  marginBottom: transcriptionExpanded ? 2 : 0,
                  '&:hover': {
                    backgroundColor: 'rgba(87, 87, 176, 0.05)',
                    borderRadius: '8px',
                    padding: '4px',
                    margin: '-4px',
                    marginBottom: transcriptionExpanded ? '12px' : '-4px',
                  }
                }} onClick={() => setTranscriptionExpanded(!transcriptionExpanded)}>
                  <Typography variant="h6" sx={{ 
                    color: primaryColor, 
                    fontWeight: 600, 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <SubtitlesIcon sx={{ fontSize: '1.2rem' }} />
                    {t('viewer.audio_transcription')}
                    {transcriptionLoading && (
                      <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center' }}>
                        <LinearProgress sx={{ width: 60, height: 3 }} />
                      </Box>
                    )}
                  </Typography>
                  <IconButton size="small" sx={{ color: primaryColor }}>
                    {transcriptionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                
                <Collapse in={transcriptionExpanded}>
                  {transcriptionLoading ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <LinearProgress sx={{ mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {t('viewer.loading_audio')}
                      </Typography>
                    </Box>
                  ) : hasTranscription && transcription ? (
                    <Box sx={{ position: 'relative' }}>
                      <TranscriptionText>
                        {transcription}
                      </TranscriptionText>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('viewer.no_transcription')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        Click the button below to generate transcription.
                      </Typography>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          requestTranscription();
                        }}
                        sx={{
                          mt: 1,
                          backgroundColor: primaryColor,
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#4a4a9a',
                          },
                        }}
                      >
                        <SubtitlesIcon />
                      </IconButton>
                    </Box>
                  )}
                </Collapse>
              </TranscriptionContainer>
            </Box>
          ) : (
            <Typography color="text.secondary">
              No audio content available
            </Typography>
          )}
        </AudioContainer>
      </ViewerDialogContent>
    </StyledDialog>
  );
});

// Set display name for debugging
AudioViewer.displayName = 'AudioViewer';

export default AudioViewer;
